import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import { CredentialKind } from "./Credential.ts";

/**
 * How the hosted collection page asks for the secret.
 *
 * - `form`: the person pastes the value into a field
 * - `oauth`: the page bounces them through the provider's OAuth flow
 *
 * Stored as plain varchar, never a pgEnum — see {@link CredentialKind}.
 *
 * @since 1.0.0
 */
export const CollectionMode = Schema.Literal("form", "oauth");
export type CollectionMode = Schema.Schema.Type<typeof CollectionMode>;

/**
 * Credential collection link model — the single-use, 7-day capability that lets
 * one person hand one credential to the vault (OL-63).
 *
 * `linkTokenHash` is {@link Model.Sensitive}: only the SHA-256 digest of the
 * capability is ever persisted, and even the digest is dropped from every JSON
 * variant, so a handler that serves `CredentialCollectionLink.json` cannot put
 * a lookup key on the wire. The plaintext is shown to the person once, at mint
 * time, and never stored.
 *
 * `personId` is the human whose credential the link collects; `createdByPersonId`
 * is whoever minted it. `customerId` is the scope, never the owner. None of the
 * three carries a foreign key — `people` / `customers` live in `public`.
 *
 * @since 1.0.0
 */
export class CredentialCollectionLink extends Model.Class<CredentialCollectionLink>("CredentialCollectionLink")({
  id: Model.Generated(Schema.String),

  personId: Schema.String,
  customerId: Schema.String,

  /** What is being asked for. `null` when the page lets the person choose. */
  kind: Schema.NullOr(CredentialKind),
  /** Free-form provider hint for the collection page. */
  provider: Schema.NullOr(Schema.String),

  mode: CollectionMode,

  /** Who minted the link — an operator, or the person themselves. */
  createdByPersonId: Schema.String,

  /** SHA-256 hex of the collection capability. Absent from every JSON variant. */
  linkTokenHash: Model.Sensitive(Schema.String),

  expiresAt: Schema.DateTimeUtc,
  /** Stamped by the atomic `consume`. `null` = still redeemable. */
  consumedAt: Schema.NullOr(Schema.DateTimeUtc),
  /** The credential the redemption created, stamped in that same UPDATE. */
  credentialId: Schema.NullOr(Schema.String),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
